import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';

import useFetchBulkHistory from 'api/useFetchBulkHistory';

import { REQUEST_TYPE } from 'constants/api';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';
import { axiosApiRequest } from 'helpers/api';
import { formatNotificationTimestamp } from 'helpers/format';
import { trackMenuBulkPriceUpdated } from 'helpers/menuAnalytics';
import { createMenuManagementAxiosInstance } from 'createAxiosInstance';

import { isNotEmpty, useForm } from '@mantine/form';

import { Card, Center, Drawer, Loader, Text } from '@mantine/core';
import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';

import { BulkForm } from './BulkForm';
import { BulkDockedButtons } from './BulkDockedButtons';
import BulkHistory from './BulkHistory';
import ConfirmRevert from './ConfirmRevert';
import ConfirmUpdate from './ConfirmUpdate';
import ConfirmUndo from './ConfirmUndo';

const DRAWER_VIEW = {
  form: 'form',
  confirmRevert: 'confirmRevert',
  confirmUpdate: 'confirmUpdate',
  confirmUndo: 'confirmUndo',
};

const SCOPE_BY_ITEM_TYPE = {
  both: 'both',
  cuboh_main_item: 'items',
  cuboh_modifier: 'modifiers',
} as const;

interface BulkDrawerProps {
  companyId: string;
  menuUuid: string;
  merchantId: string;
  opened: boolean;
  onClose: () => void;
}

export default function EditByBulkDrawer({
  companyId,
  menuUuid,
  merchantId,
  opened,
  onClose,
}: BulkDrawerProps) {
  const [drawerView, setDrawerView] = useState(DRAWER_VIEW.form);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let el: HTMLElement | null = topRef.current?.parentElement ?? null;
    while (el) {
      if (el.scrollTop > 0) {
        el.scrollTop = 0;
        break;
      }
      el = el.parentElement;
    }
  }, [drawerView]);

  const isFormView = drawerView === DRAWER_VIEW.form;
  const isConfirmUpdateView = drawerView === DRAWER_VIEW.confirmUpdate;
  const isConfirmUndoView = drawerView === DRAWER_VIEW.confirmUndo;
  const isConfirmRevertView = drawerView === DRAWER_VIEW.confirmRevert;

  const formValidation = isFormView || isConfirmUpdateView ? isNotEmpty() : undefined;

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      allItems: false,
      allModifiers: false,
      requiredChange: '',
      updateBy: '',
      updateType: 'percentage',
    },
    validate: {
      requiredChange: formValidation,
      updateBy: formValidation,
    },
  });

  const formValues = form.getValues();

  const { bulkHistory, fetchError, isLoadingHistory } = useFetchBulkHistory({
    companyId,
    menuUuid,
    merchantId,
    shouldFetch: opened,
  });

  const hasBulkHistory = !!bulkHistory.length;

  const onDrawerClose = () => {
    onClose();
    setDrawerView(DRAWER_VIEW.form);
    form.reset();
  };

  const onUpdateSuccess = () => {
    notifications.show({
      message: `You have successfully updated pricing on ${formatNotificationTimestamp()}.`,
      variant: NOTIFICATION_VARIANTS.success.label,
      icon: NOTIFICATION_VARIANTS.success.icon,
      style: { marginTop: 'var(--mantine-spacing-sm)' },
    });
    onDrawerClose();
  };

  const onUpdateFailure = () => {
    notifications.show({
      message: 'We could not update your menu. Please contact customer support.',
      variant: NOTIFICATION_VARIANTS.caution.label,
      icon: NOTIFICATION_VARIANTS.caution.icon,
      style: { marginTop: 'var(--mantine-spacing-sm)' },
    });
    onDrawerClose();
  };

  function getUpdateItemType() {
    if (formValues.allItems && formValues.allModifiers) {
      return 'both';
    }

    if (formValues.allItems) {
      return 'cuboh_main_item';
    }

    return 'cuboh_modifier';
  }

  const handleButtonClick = async () => {
    if (isFormView) {
      setDrawerView(DRAWER_VIEW.confirmUpdate);
    }

    if (isConfirmUpdateView) {
      const incOrDec = formValues.updateBy === 'decrease' ? '-' : '';
      const amount =
        formValues.updateType === 'percentage'
          ? formValues.requiredChange
          : parseFloat(formValues.requiredChange) * 100; // BE expects cents, not dollar amount
      const formattedData = {
        menuUuid,
        requiredChange: `${incOrDec}${amount}`,
        updateItemType: getUpdateItemType(),
        updateType: formValues.updateType,
      };

      const data = await axiosApiRequest({
        createAxiosInstance: createMenuManagementAxiosInstance,
        method: REQUEST_TYPE.PATCH,
        path: `company/${companyId}/cuboh/merchant/${merchantId}/markup/price`,
        data: formattedData,
        setError: onUpdateFailure,
      });

      if (data) {
        onUpdateSuccess();

        trackMenuBulkPriceUpdated({
          menuId: menuUuid,
          direction: formValues.updateBy === 'decrease' ? 'decrease' : 'increase',
          updateType: formValues.updateType === 'percentage' ? 'percentage' : 'fixed_cents',
          scope: SCOPE_BY_ITEM_TYPE[getUpdateItemType()],
        });
      }
    }

    if (isConfirmUndoView || isConfirmRevertView) {
      let formattedData: { menuUuid: string; revertOriginal: boolean; logUuid?: string } = {
        menuUuid,
        revertOriginal: isConfirmRevertView,
      };

      if (isConfirmUndoView) {
        formattedData = {
          ...formattedData,
          logUuid: bulkHistory[0].logUuid,
        };
      }

      const data = await axiosApiRequest({
        createAxiosInstance: createMenuManagementAxiosInstance,
        method: REQUEST_TYPE.PATCH,
        path: `company/${companyId}/cuboh/merchant/${merchantId}/markup/price/revert`,
        data: formattedData,
        setError: onUpdateFailure,
      });

      if (data) {
        onUpdateSuccess();
      }
    }
  };

  const isButtonDisabled = !(formValues.allItems || formValues.allModifiers) || !form.isValid();

  if (isLoadingHistory) {
    return (
      <Drawer title="Edit by Bulk" opened={opened} onClose={onDrawerClose} variant="withDock">
        <Center p="md">
          <Loader />
        </Center>
      </Drawer>
    );
  }

  if (fetchError) {
    return (
      <Drawer title="Edit by Bulk" opened={opened} onClose={onDrawerClose} variant="withDock">
        <DockedDrawerFormContent>
          <Card p="xl" bg="var(--surface-secondary)">
            <Center>
              {/* TODO: use GENERIC_QUERY_ERROR_MESSAGE from 'constants/error' */}
              <Text variant="sublabel-sm">Oops! There was an issue loading your data.</Text>
            </Center>
          </Card>
        </DockedDrawerFormContent>
      </Drawer>
    );
  }

  return (
    <Drawer title="Edit by Bulk" opened={opened} onClose={onDrawerClose} variant="withDock">
      <form onSubmit={form.onSubmit(handleButtonClick)}>
        <div ref={topRef} />
        <DockedDrawerFormContent
          dockButtons={
            <BulkDockedButtons
              isFormView={isFormView}
              hasBulkHistory={hasBulkHistory}
              isButtonDisabled={isButtonDisabled}
              onBack={() => setDrawerView(DRAWER_VIEW.form)}
              onRevert={() => setDrawerView(DRAWER_VIEW.confirmRevert)}
              onUndo={() => setDrawerView(DRAWER_VIEW.confirmUndo)}
            />
          }
        >
          {isFormView && <BulkForm form={form} />}
          {isFormView && hasBulkHistory && (
            <BulkHistory
              history={bulkHistory}
              onUndo={() => setDrawerView(DRAWER_VIEW.confirmUndo)}
            />
          )}
          {isConfirmUpdateView && <ConfirmUpdate formData={formValues} />}
          {isConfirmUndoView && hasBulkHistory && <ConfirmUndo lastUpdate={bulkHistory[0]} />}
          {isConfirmRevertView && hasBulkHistory && (
            <ConfirmRevert originalDate={bulkHistory[bulkHistory.length - 1].timestamp} />
          )}
        </DockedDrawerFormContent>
      </form>
    </Drawer>
  );
}
